/**
 * Chasing money that hasn't arrived (PRD v2 §2.2 — late fees + dunning retries). Pure,
 * because both answers charge a real customer real money on a schedule nobody is watching.
 *
 * Everything here is a PLAN, not an act: `plannedLateFee` says what would be charged and
 * when, and the same function feeds the nightly sweep AND the "here's what happens next"
 * line on the invoice. One definition means the warning a shop reads is arithmetically the
 * charge they get — not a second implementation that drifts.
 */
import { addDaysYmd } from './mcp/timezone';
import { dayCount, dueSteps, type PlannedStep } from './automations';

export interface InvoicePolicy {
  lateFeeEnabled: boolean;
  lateFeeKind: 'flat' | 'percent';
  lateFeeFlatCents: number;
  lateFeePercentBps: number;
  lateFeeGraceDays: number;
  /** 0 = charge once and stop. Otherwise the gap between repeat charges. */
  lateFeeIntervalDays: number;
  lateFeeMaxCount: number;
  lateFeeMinBalanceCents: number;
  remindersEnabled: boolean;
  reminderOffsetDays: number[];
}

/** What every shop starts with: the machinery installed, every switch off. */
export const DEFAULT_INVOICE_POLICY: InvoicePolicy = {
  lateFeeEnabled: false,
  lateFeeKind: 'percent',
  lateFeeFlatCents: 0,
  lateFeePercentBps: 150,
  lateFeeGraceDays: 5,
  lateFeeIntervalDays: 30,
  lateFeeMaxCount: 3,
  lateFeeMinBalanceCents: 2500,
  remindersEnabled: false,
  reminderOffsetDays: [3, 10, 21],
};

/** The shape the fee math needs off an invoice — narrow on purpose, so it stays testable. */
export interface CollectableInvoice {
  status: string;
  dueDate: string | null;
  totalCents: number;
  lateFeeCents: number;
  amountPaidCents: number;
}

/**
 * THE balance definition. An issued invoice is immutable, so late fees can't be line items —
 * they roll up in `lateFeeCents` and land here instead. Every screen that says "owes" must
 * call this; seven hand-rolled subtractions is how a shop ends up quoting two numbers.
 */
export function invoiceBalanceCents(invoice: {
  totalCents: number;
  lateFeeCents?: number;
  amountPaidCents: number;
}): number {
  return invoice.totalCents + (invoice.lateFeeCents ?? 0) - invoice.amountPaidCents;
}

/** An invoice is chaseable only while it is issued, unpaid and has a date to be late against. */
export function isChaseable(invoice: CollectableInvoice): boolean {
  if (invoice.status !== 'issued' && invoice.status !== 'partially_paid') return false;
  if (!invoice.dueDate) return false;
  return invoiceBalanceCents(invoice) > 0;
}

export interface PlannedLateFee {
  /** The date this fee becomes chargeable. In the future = the warning; today or past = due. */
  dueOn: string;
  amountCents: number;
  basisBalanceCents: number;
  kind: 'flat' | 'percent';
  sequence: number;
}

export interface AppliedFee {
  sequence: number;
  appliedOn: string;
  waivedAt: Date | string | null;
}

/**
 * The next late fee this invoice is heading for, or null if it isn't heading for one.
 *
 * A percent fee is taken on the OUTSTANDING balance, not the original total: a customer who
 * paid four fifths shouldn't be charged as though they'd paid none. And the amount is
 * recomputed each period rather than repeated, so a partial payment immediately shrinks the
 * next fee — the behaviour a customer would call fair if they ever asked how it worked.
 *
 * WAIVED fees still occupy their sequence number. Waiving forgives a charge; it does not
 * hand back a slot for the sweep to charge again next week.
 */
export function plannedLateFee(
  policy: InvoicePolicy,
  invoice: CollectableInvoice,
  fees: AppliedFee[],
): PlannedLateFee | null {
  if (!policy.lateFeeEnabled) return null;
  if (!isChaseable(invoice)) return null;

  const balance = invoiceBalanceCents(invoice);
  if (balance < policy.lateFeeMinBalanceCents) return null;
  if (fees.length >= policy.lateFeeMaxCount) return null;

  const dueDate = invoice.dueDate!;
  let dueOn: string;
  if (fees.length === 0) {
    dueOn = addDaysYmd(dueDate, policy.lateFeeGraceDays);
  } else {
    // One-and-done: the shop said "charge a fee", not "charge a fee forever".
    if (policy.lateFeeIntervalDays === 0) return null;
    const last = fees.reduce((a, b) => (a.appliedOn > b.appliedOn ? a : b));
    dueOn = addDaysYmd(last.appliedOn, policy.lateFeeIntervalDays);
  }

  const amountCents =
    policy.lateFeeKind === 'flat'
      ? policy.lateFeeFlatCents
      : Math.round((balance * policy.lateFeePercentBps) / 10_000);
  if (amountCents <= 0) return null;

  return {
    dueOn,
    amountCents,
    basisBalanceCents: balance,
    kind: policy.lateFeeKind,
    sequence: fees.length + 1,
  };
}

export type PlannedReminder = PlannedStep;

/**
 * Which reminder steps are due today, and which have been overtaken.
 *
 * The scheduling rule itself now lives in `dueSteps` — quote follow-ups need exactly the
 * same "at most one fires, the latest wins, the rest are burned" behaviour, and two copies
 * of it would eventually disagree about what "already chased" means. What stays here is the
 * part that is specific to money: reminders off, or an invoice that is not chaseable
 * (already paid, voided, or with no due date to count from) means nothing is due at all.
 */
export function dueReminders(
  policy: InvoicePolicy,
  invoice: CollectableInvoice,
  alreadyHandled: number[],
  today: string,
): PlannedReminder[] {
  if (!policy.remindersEnabled) return [];
  if (!isChaseable(invoice)) return [];
  return dueSteps(policy.reminderOffsetDays, invoice.dueDate!, alreadyHandled, today);
}

/** How a reminder step reads to a person: "3 days late", "due in 3 days", "due today". */
export function reminderStepLabel(offsetDays: number): string {
  if (offsetDays === 0) return 'the day it is due';
  if (offsetDays < 0) return `${dayCount(-offsetDays)} before due`;
  return `${dayCount(offsetDays)} after due`;
}
